export type BehavioralPatternGraphInfo = {
  width: number;
  height: number;
  spacing: number;
  squareWidth: number;
  circleRadius: number;
  eSigmaBarHeight: number;
  strokeWidth: number;
};

/**
 * As more types of behavioral pattern graphs are added, they can be added
 * here. Although, this service might need to be moved somewhere else in that
 * case to make more sense.
 */
export enum BehavioralPatternGraphType {
  Mini = 'mini',
  Standard = 'standard',
}

export interface CombinedSigmas {
  firstPersonCurrentFactorSigma: number;
  firstPersonNextFactorSigma: number;
  secondPersonCurrentFactorSigma: number;
  secondPersonNextFactorSigma: number;
}

/**
 * All the values contained below were pulled from `ba.constants.ts` in
 * pi-behavioralassessment-report-lib.
 */
export const behavioralPatternGraphs: Record<BehavioralPatternGraphType, BehavioralPatternGraphInfo> = {
  mini: {
    width: 150,
    height: 8,
    spacing: 5,
    squareWidth: 8,
    circleRadius: 4,
    eSigmaBarHeight: 1,
    strokeWidth: 1.25,
  },
  standard: {
    width: 500,
    height: 20,
    spacing: 9,
    squareWidth: 18,
    circleRadius: 10,
    eSigmaBarHeight: 2,
    strokeWidth: 2,
  },
};

export enum BAFactorShapeType {
  Circle,
  Square,
}

/**
 * A service which provides methods for calculating the x and y values of
 * behavioral pattern graph shapes.
 */
export default class FactorShapeService {
  private static readonly totalSigmasOnRangeBar = 6;
  private static readonly sigmasBelowMidpointOnRangeBar = 3;

  static getFactorShapeXValue(
    sigmaValue: number,
    graphInfo: BehavioralPatternGraphInfo,
    shapeType: BAFactorShapeType
  ): number {
    const rangeBarWidth = graphInfo.width;

    const displayValue = this.getXValue(sigmaValue, rangeBarWidth);

    if (shapeType === BAFactorShapeType.Circle) {
      return this.adjustCircleDisplayValueForEdgesOfGraph(displayValue, graphInfo.circleRadius, rangeBarWidth);
    } else {
      return this.adjustRectDisplayValueForEdgesOfGraphAndXOffset(
        displayValue,
        graphInfo.squareWidth / 2,
        rangeBarWidth
      );
    }
  }

  private static adjustCircleDisplayValueForEdgesOfGraph(
    displayValue: number,
    circleRadius: number,
    rangeBarWidth: number
  ) {
    if (displayValue > rangeBarWidth - circleRadius) {
      displayValue = rangeBarWidth - circleRadius;
    }
    if (displayValue < circleRadius) {
      displayValue = circleRadius;
    }

    return displayValue;
  }

  private static adjustRectDisplayValueForEdgesOfGraphAndXOffset(
    displayValue: number,
    rectangleWidthFromCenter: number,
    rangeBarWidth: number
  ) {
    if (displayValue > rangeBarWidth - rectangleWidthFromCenter) {
      displayValue = rangeBarWidth - rectangleWidthFromCenter;
    }
    if (displayValue < rectangleWidthFromCenter) {
      displayValue = rectangleWidthFromCenter;
    }

    return displayValue;
  }

  private static getXValue(sigmaValue: number, rangeBarWidth: number) {
    const xValue = ((sigmaValue + this.sigmasBelowMidpointOnRangeBar) / this.totalSigmasOnRangeBar) * rangeBarWidth;
    return xValue;
  }
}
